import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Crown, Star, Sparkles, Search, X, BookOpen, ArrowRight, Users, PlayCircle } from 'lucide-react';
import axios from 'axios';
import BookCover from '../components/BookCover';
import LandingHero from '../components/LandingHero';
import ReadingStreak from '../components/ReadingStreak';
import OnboardingModal from '../components/OnboardingModal';
import SocialProofHero from '../components/SocialProofHero';
import SEO from '../components/SEO';
import { Skeleton } from '../components/ui/skeleton';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { useSubscription } from '../context/SubscriptionContext';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useProfiles } from '../context/ProfileContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SCROLL_KEY = 'libraryScrollY';

const Library = () => {
  const navigate = useNavigate();
  const { isAuthenticated, token } = useAuth();
  const { isPremium, isTrialing, trialDaysLeft, subscription } = useSubscription();
  const { activeProfile } = useProfiles();
  const { t, lang } = useLanguage();
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [grandmas, setGrandmas] = useState([]);
  const [grandmasWithCounts, setGrandmasWithCounts] = useState([]);
  const [selectedGrandma, setSelectedGrandma] = useState('');
  const [favorites, setFavorites] = useState([]);
  const [readingProgress, setReadingProgress] = useState({});
  const [continueReading, setContinueReading] = useState([]);
  const [recommendations, setRecommendations] = useState([]);

  useEffect(() => {
    fetchStories();
    fetchGrandmas();
    fetchGrandmasWithCounts();
    if (isAuthenticated) {
      fetchFavorites();
      fetchReadingProgress();
      fetchRecommendations();
      fetchContinueReading();
    }
  }, [isAuthenticated, lang]);

  // Re-fetch when search query changes (debounced via backend search)
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchStories();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Re-fetch stories when the tab regains focus, so nieuwe verhalen
  // (toegevoegd door admins) meteen zichtbaar worden zonder refresh.
  useEffect(() => {
    const onFocus = () => fetchStories();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') fetchStories();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang, searchQuery]);

  const fetchStories = async () => {
    try {
      const params = new URLSearchParams({ approved_only: 'true' });
      if (lang) params.append('lang', lang);
      if (searchQuery) params.append('search', searchQuery);
      const response = await axios.get(`${API}/stories?${params.toString()}`);
      setStories(response.data);
    } catch (error) {
      console.error('Failed to fetch stories:', error);
    } finally {
      setLoading(false);
    }
  };

  // Bewaar scroll-positie bij verlaten van de bibliotheek, herstel bij terugkomst
  useEffect(() => {
    const onLeave = () => sessionStorage.setItem(SCROLL_KEY, String(window.scrollY));
    window.addEventListener('beforeunload', onLeave);
    return () => {
      onLeave();
      window.removeEventListener('beforeunload', onLeave);
    };
  }, []);

  useEffect(() => {
    if (loading) return;
    const saved = parseInt(sessionStorage.getItem(SCROLL_KEY) || '0', 10);
    if (saved > 0) {
      // wacht 1 tick zodat de DOM gerenderd is
      requestAnimationFrame(() => {
        window.scrollTo({ top: saved, behavior: 'auto' });
        sessionStorage.removeItem(SCROLL_KEY);
      });
    }
  }, [loading]);

  const fetchGrandmas = async () => {
    try {
      const response = await axios.get(`${API}/grandmas`);
      const unique = response.data.reduce((acc, g) => {
        if (!acc.find(x => x.name === g.name)) acc.push(g);
        return acc;
      }, []);
      setGrandmas(unique);
    } catch (error) {
      console.error('Failed to fetch grandmas:', error);
    }
  };

  const fetchGrandmasWithCounts = async () => {
    try {
      const response = await axios.get(`${API}/grandmas/with-counts`);
      setGrandmasWithCounts(response.data);
    } catch (error) {
      console.error('Failed to fetch grandmas with counts:', error);
    }
  };

  const fetchFavorites = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/favorites`, { headers });
      setFavorites(response.data);
    } catch (error) { /* ignore */ }
  };

  const fetchReadingProgress = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/reading-progress`, { headers });
      setReadingProgress(response.data);
    } catch (error) { /* ignore */ }
  };

  const fetchRecommendations = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/recommendations?lang=${lang}`, { headers });
      setRecommendations(response.data.recommendations || []);
    } catch (error) { /* ignore */ }
  };

  const fetchContinueReading = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/reading-progress/continue`, { headers });
      setContinueReading(response.data);
    } catch (error) { /* ignore */ }
  };

  const toggleFavorite = async (storyId) => {
    if (!isAuthenticated) { navigate('/login'); return; }
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    if (favorites.includes(storyId)) {
      await axios.delete(`${API}/favorites/${storyId}`, { headers });
      setFavorites(favorites.filter(id => id !== storyId));
    } else {
      await axios.post(`${API}/favorites/${storyId}`, {}, { headers });
      setFavorites([...favorites, storyId]);
    }
  };

  const filteredStories = stories.filter(s => {
    const matchesGrandma = !selectedGrandma || s.grandma_id === selectedGrandma;
    return matchesGrandma;
  });

  const freeStories = filteredStories.filter(s => s.is_free);
  const premiumStories = filteredStories.filter(s => !s.is_free);

  return (
    <div className="min-h-screen" data-testid="library-page">
      <SEO path="/" />
      <div className="pt-4 md:pt-20">
        <LandingHero />
      </div>

      {/* Onboarding for new authenticated users */}
      {isAuthenticated && <OnboardingModal />}

      <div className="px-4 pb-48 md:pb-8">
        {/* Sociale proof hero — altijd zichtbaar, trial CTA alleen voor niet-premium */}
        <div className="max-w-3xl mx-auto pt-2">
          <SocialProofHero showTrialCta={!isPremium && !isTrialing} />
        </div>

        {isAuthenticated && (
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-6 pt-4 md:pt-4">
            {activeProfile ? (
              <h1 data-testid="library-greeting" className="font-display text-5xl md:text-7xl text-foreground mb-4">
                Hoi <span style={{ color: activeProfile.color || '#db8f2d' }}>{activeProfile.name}</span> {activeProfile.avatar}
              </h1>
            ) : (
              <h1 className="font-display text-5xl md:text-7xl text-foreground mb-4">{t('library.title')}</h1>
            )}
            <p className="text-muted-foreground text-lg md:text-xl font-accent max-w-lg mx-auto mb-6">{t('library.subtitle')}</p>
            <ReadingStreak />
          </motion.div>
        )}

        {!isAuthenticated && (
          <div id="bookshelf-section" className="text-center mb-8 pt-4">
            <h2 className="font-display text-3xl md:text-4xl text-foreground mb-2">{t('library.our_stories')}</h2>
            <p className="text-muted-foreground">{t('library.our_stories_sub')}</p>
          </div>
        )}

        {isTrialing && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="max-w-2xl mx-auto mb-8">
            <div className="bg-gold/10 border border-gold/30 rounded-2xl p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Crown className="w-6 h-6 text-gold" />
                <div>
                  <p className="font-bold text-foreground">{t('library.trial_active')}</p>
                  <p className="text-sm text-muted-foreground">{trialDaysLeft} {t('library.trial_days_left')}</p>
                </div>
              </div>
              <Button onClick={() => navigate('/subscription')} size="sm" className="rounded-full bg-gold text-night-blue hover:bg-gold/90">
                {t('library.view_sub')}
              </Button>
            </div>
          </motion.div>
        )}

        {isAuthenticated && !isPremium && !loading && stories.length > 0 && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="max-w-2xl mx-auto mb-8">
            <div className="bg-gradient-to-r from-old-rose/20 to-gold/20 border border-gold/30 rounded-2xl p-4 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3 text-center sm:text-left">
                <Crown className="w-8 h-8 text-gold hidden sm:block" />
                <div>
                  <p className="font-bold text-foreground">{t('library.become_premium')}</p>
                  <p className="text-sm text-muted-foreground">
                    {subscription?.trial_available ? t('library.trial_cta') : t('library.premium_cta')}
                  </p>
                </div>
              </div>
              <Button onClick={() => navigate('/subscription')} className="rounded-full bg-gold text-night-blue hover:bg-gold/90 font-bold whitespace-nowrap">
                {subscription?.trial_available ? t('library.try_free') : t('library.view_premium')}
              </Button>
            </div>
          </motion.div>
        )}

        {/* Lees verder */}
        {isAuthenticated && continueReading.length > 0 && !searchQuery && !selectedGrandma && (
          <div className="max-w-6xl mx-auto mb-8" data-testid="continue-reading-section">
            <div className="flex items-center gap-2 mb-4 px-4">
              <PlayCircle className="w-5 h-5 text-gold" />
              <h2 className="font-display text-2xl text-foreground">{t('library.continue')}</h2>
            </div>
            <div className="flex gap-4 overflow-x-auto px-4 pb-2 scrollbar-hide">
              {continueReading.map((item, index) => (
                <motion.div
                  key={item.story.id}
                  data-testid={`continue-${item.story.id}`}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  onClick={() => navigate(`/story/${item.story.id}`)}
                  className="group flex-shrink-0 w-64 bg-card rounded-2xl overflow-hidden border-2 border-border hover:border-gold/40 cursor-pointer transition-all hover:shadow-md"
                >
                  <div className="relative h-28">
                    {item.story.cover_image_url ? (
                      <img src={item.story.cover_image_url} alt={item.story.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-old-rose/40 to-gold/30 flex items-center justify-center">
                        <BookOpen className="w-8 h-8 text-white/40" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    <div className="absolute bottom-2 left-3 right-3">
                      <h3 className="font-display text-base text-cream truncate">{item.story.title}</h3>
                    </div>
                  </div>
                  <div className="p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs text-muted-foreground font-accent">{item.story.grandma_name}</span>
                      <span className="text-xs font-bold text-gold">{item.progress_percent}%</span>
                    </div>
                    <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                      <div className="h-full bg-gold rounded-full transition-all" style={{ width: `${item.progress_percent}%` }} />
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {/* Recommendations */}
        {isAuthenticated && recommendations.length > 0 && !searchQuery && !selectedGrandma && (
          <div className="max-w-6xl mx-auto mb-10">
            <h2 className="font-display text-2xl text-foreground mb-1 px-4">{t('library.recommended')}</h2>
            <p className="text-sm text-muted-foreground mb-4 px-4">{t('library.recommended_sub')}</p>
            <div className="relative">
              <div className="absolute -bottom-4 left-0 right-0 h-4 bg-gradient-to-b from-purple-800/20 to-purple-900/40 rounded-b-lg shadow-lg" />
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6 md:gap-8 p-4 md:p-8 bg-gradient-to-b from-purple-900/10 to-purple-800/15 rounded-t-2xl border border-purple-500/10">
                {recommendations.slice(0, 5).map((story, index) => (
                  <BookCover key={story.id} story={story} index={index} isFavorite={favorites.includes(story.id)} onToggleFavorite={toggleFavorite} progress={readingProgress[story.id]} />
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Oma's Sectie */}
        {grandmasWithCounts.length > 0 && !searchQuery && !selectedGrandma && (
          <div className="max-w-6xl mx-auto mb-8" data-testid="library-omas-section">
            <div className="flex items-center justify-between mb-4 px-4">
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-old-rose" />
                <h2 className="font-display text-2xl text-foreground">{t('omas.title')}</h2>
              </div>
              <Link to="/omas">
                <Button variant="ghost" size="sm" className="rounded-full text-muted-foreground hover:text-foreground" data-testid="view-all-omas">
                  {t('omas.view_stories')} <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </div>
            <div className="flex gap-4 overflow-x-auto px-4 pb-2 scrollbar-hide">
              {grandmasWithCounts.map((grandma, index) => (
                <motion.div
                  key={grandma.id}
                  data-testid={`library-oma-${grandma.id}`}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.08 }}
                  onClick={() => navigate(`/grandma/${grandma.id}`)}
                  className="group flex-shrink-0 w-52 bg-card rounded-2xl p-4 border-2 border-border hover:border-gold/40 cursor-pointer transition-all hover:shadow-md hover:-translate-y-0.5"
                >
                  <div className="flex flex-col items-center text-center gap-2">
                    <Avatar className="w-14 h-14 border-2 border-gold/20 group-hover:border-gold/40 transition-colors">
                      <AvatarImage src={grandma.photo_url} alt={grandma.name} />
                      <AvatarFallback className="bg-old-rose/30 text-foreground text-xl font-display">
                        {grandma.name?.charAt(0)}
                      </AvatarFallback>
                    </Avatar>
                    <h3 className="font-display text-lg text-foreground truncate w-full group-hover:text-gold transition-colors">
                      {grandma.name}
                    </h3>
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <BookOpen className="w-3 h-3" />
                      <span className="font-accent">{grandma.story_count} {grandma.story_count === 1 ? t('omas.story') : t('omas.stories')}</span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {/* Search & Filter */}
        <div className="max-w-6xl mx-auto mb-6">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input data-testid="library-search" placeholder={t('library.search')} value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10 rounded-full" />
              {searchQuery && (
                <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <div className="flex gap-2">
              {grandmas.length > 0 && (
                <select data-testid="library-grandma-filter" value={selectedGrandma} onChange={(e) => setSelectedGrandma(e.target.value)} className="rounded-full border border-input bg-background px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring">
                  <option value="">{t('library.all_grandmas')}</option>
                  {grandmas.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                </select>
              )}
            </div>
          </div>
          {(searchQuery || selectedGrandma) && (
            <p className="text-sm text-muted-foreground mt-2 px-1">
              {filteredStories.length} {filteredStories.length === 1 ? t('library.story_found') : t('library.found')}
              {searchQuery && <> {t('library.for')} "{searchQuery}"</>}
              {selectedGrandma && <> {t('library.of')} {grandmas.find(g => g.id === selectedGrandma)?.name}</>}
            </p>
          )}
        </div>

        {/* Bookshelf */}
        <div className="max-w-6xl mx-auto">
          {loading ? (
            <div className="relative">
              <div className="absolute -bottom-4 left-0 right-0 h-4 bg-gradient-to-b from-amber-800/40 to-amber-900/60 rounded-b-lg shadow-lg" />
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6 md:gap-8 p-4 md:p-8 bg-gradient-to-b from-amber-900/10 to-amber-800/20 rounded-t-2xl">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="aspect-[3/4]"><Skeleton className="w-full h-full rounded-r-xl rounded-l-sm" /></div>
                ))}
              </div>
            </div>
          ) : filteredStories.length === 0 ? (
            <div className="relative">
              <div className="absolute -bottom-4 left-0 right-0 h-4 bg-gradient-to-b from-amber-800/40 to-amber-900/60 rounded-b-lg shadow-lg" />
              <div className="p-4 md:p-8 bg-gradient-to-b from-amber-900/10 to-amber-800/20 rounded-t-2xl">
                <div className="text-center py-12">
                  <Search className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="font-display text-2xl text-foreground mb-2">
                    {searchQuery || selectedGrandma ? t('library.no_results') : t('library.empty')}
                  </h3>
                  <p className="text-muted-foreground font-accent">
                    {searchQuery || selectedGrandma ? t('library.try_other') : t('library.coming_soon')}
                  </p>
                  {(searchQuery || selectedGrandma) && (
                    <Button variant="outline" className="mt-4 rounded-full" onClick={() => { setSearchQuery(''); setSelectedGrandma(''); }}>
                      {t('library.clear_filters')}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <>
              {freeStories.length > 0 && (
                <div className="mb-8">
                  <h2 className="font-display text-2xl text-foreground mb-4 px-4">{t('library.free')}</h2>
                  <div className="relative">
                    <div className="absolute -bottom-4 left-0 right-0 h-4 bg-gradient-to-b from-amber-800/40 to-amber-900/60 rounded-b-lg shadow-lg" />
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6 md:gap-8 p-4 md:p-8 bg-gradient-to-b from-amber-900/10 to-amber-800/20 rounded-t-2xl">
                      {freeStories.map((story, index) => (
                        <BookCover key={story.id} story={story} index={index} isFavorite={favorites.includes(story.id)} onToggleFavorite={toggleFavorite} progress={readingProgress[story.id]} />
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {premiumStories.length > 0 && (
                <div className="mt-12">
                  <div className="flex items-center gap-2 mb-4 px-4">
                    <Crown className="w-6 h-6 text-gold" />
                    <h2 className="font-display text-2xl text-foreground">{t('library.premium')}</h2>
                    {!isPremium && <span className="text-sm text-muted-foreground ml-2">{t('library.premium_required')}</span>}
                  </div>
                  <div className="relative">
                    <div className="absolute -bottom-4 left-0 right-0 h-4 bg-gradient-to-b from-amber-800/40 to-amber-900/60 rounded-b-lg shadow-lg" />
                    <div className={`grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6 md:gap-8 p-4 md:p-8 bg-gradient-to-b from-amber-900/10 to-amber-800/20 rounded-t-2xl ${!isPremium ? 'opacity-75' : ''}`}>
                      {premiumStories.map((story, index) => (
                        <BookCover key={story.id} story={story} index={index} locked={!isPremium} isFavorite={favorites.includes(story.id)} onToggleFavorite={toggleFavorite} progress={readingProgress[story.id]} />
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          <div className="flex justify-center gap-4 mt-8">
            <motion.div animate={{ y: [0, -5, 0] }} transition={{ duration: 2, repeat: Infinity }}><Star className="w-6 h-6 text-gold fill-gold" /></motion.div>
            <motion.div animate={{ y: [0, -5, 0] }} transition={{ duration: 2, repeat: Infinity, delay: 0.3 }}><Sparkles className="w-5 h-5 text-gold" /></motion.div>
            <motion.div animate={{ y: [0, -5, 0] }} transition={{ duration: 2, repeat: Infinity, delay: 0.6 }}><Star className="w-6 h-6 text-gold fill-gold" /></motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Library;
